/**
 * Presence: who else is looking at a World, and where their hand is.
 *
 * Presence is motion, never matter: it rides the watch socket, is never
 * stored, and the server drops what is too big or too fast. This module is
 * the client's half, and all of it is pure over an injected clock:
 *
 * - the codec: PresenceIn frames checked, clamped to the server's bounds and
 *   serialised without unknown fields; PresenceOut frames read defensively,
 *   since every string in them was typed by someone else;
 * - the gate: at most 10 frames a second per socket, cursor-like frames
 *   (`at`, `over`, `focus`) coalesced so the latest wins, `focus` at most
 *   twice a second, and a heartbeat that keeps an idle viewer on others'
 *   rosters;
 * - the roster: per World, you and your peers, their latest frame, entries
 *   that expire 60 s after anyone last heard of them (a lost `left`), and one
 *   HUD entry per person (name and key tail), counted in tabs.
 */

import type { PresenceIn, PresenceOut, Roster, Unsub, Viewer } from '../contracts';
import { isValid } from '../spec/schema';
import { redactText } from '../spec/twins';
import { isObj } from '../model/json';

export interface Timers { set(fn: () => void, ms: number): unknown; clear(id: unknown): void }

/** The server's bound on one frame. */
export const FRAME_BYTES = 512;
export const FRAMES_PER_SECOND = 10;
export const FOCUS_PER_SECOND = 2;
export const HEARTBEAT_MS = 20_000;
export const ROSTER_TTL_MS = 60_000;

const WHAT = new Set(['occupier', 'run', 'softRun', 'spark', 'braid', 'bead', 'card', 'other']);
const MASK = /^0x[0-9a-f]+$/;
const HEAD = /^[0-9a-f]{32}$/;
const COLOR = /^#[0-9a-f]{6}$/;

/** A string cut to `max` characters, with anything shaped like a key cut to its prefix: a label is typed by hand. */
const clamp = (v: unknown, max: number): string | undefined => (typeof v === 'string' ? Array.from(redactText(v)).slice(0, max).join('') : undefined);
const int = (v: unknown, lo: number, hi: number): number | undefined => (typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi ? v : undefined);
const date = (v: unknown): string | undefined => (typeof v === 'string' && isValid({ type: 'string', format: 'date' }, v) ? v : undefined);
const mask = (v: unknown): string | undefined => (typeof v === 'string' && MASK.test(v) && v.length <= 34 ? v : undefined);

/** Drop the keys whose value is undefined, so a frame carries only what it says. */
const tidy = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;

/**
 * A PresenceIn, if the value is one: strings clamped to the server's bounds,
 * unknown fields dropped, and fields of the wrong shape left out (an
 * optional field) or the whole frame refused (a required one).
 */
export const readPresenceIn = (v: unknown): PresenceIn | null => {
  if (!isObj(v) || typeof v.t !== 'string') return null;
  switch (v.t) {
    case 'at':
      return tidy({ t: 'at' as const, date: date(v.date), hour: int(v.hour, 0, 23), slot: int(v.slot, 0, 59), lane: clamp(v.lane, 64) });
    case 'lift': {
      const label = clamp(v.label, 64);
      if (typeof v.what !== 'string' || !WHAT.has(v.what) || label === undefined) return null;
      return tidy({
        t: 'lift' as const, what: v.what as Extract<PresenceIn, { t: 'lift' }>['what'], label,
        op: clamp(v.op, 64), date: date(v.date), hour: int(v.hour, 0, 23), slots: mask(v.slots),
      });
    }
    case 'over':
      return tidy({ t: 'over' as const, date: date(v.date), hour: int(v.hour, 0, 23), slots: mask(v.slots), lane: clamp(v.lane, 64) });
    case 'end':
      return v.how === 'drop' || v.how === 'cancel' ? { t: 'end', how: v.how } : null;
    case 'act': {
      const op = clamp(v.op, 64), call = clamp(v.call, 40);
      const ph: 'sent' | 'settled' | null = v.ph === 'sent' ? 'sent' : v.ph === 'settled' ? 'settled' : null;
      const via: 'hand' | 'director' | 'scenario' | null = v.via === 'hand' ? 'hand' : v.via === 'director' ? 'director' : v.via === 'scenario' ? 'scenario' : null;
      if (ph === null || via === null || op === undefined || call === undefined) return null;
      return tidy({
        t: 'act' as const, ph, op, call, status: int(v.status, 0, 999),
        head: typeof v.head === 'string' && HEAD.test(v.head) ? v.head : undefined, via,
      });
    }
    case 'focus': {
      const view = clamp(v.view, 64);
      return view === undefined ? null : tidy({ t: 'focus' as const, view, date: date(v.date), hour: int(v.hour, 0, 23) });
    }
    case 'director': {
      const ph: 'start' | 'step' | 'done' | null = v.ph === 'start' ? 'start' : v.ph === 'step' ? 'step' : v.ph === 'done' ? 'done' : null;
      if (ph === null) return null;
      return tidy({ t: 'director' as const, ph, step: int(v.step, 0, Number.MAX_SAFE_INTEGER), tool: clamp(v.tool, 64) });
    }
    default:
      return null;
  }
};

const bytes = (s: string): number => new TextEncoder().encode(s).length;
/** Fields that are words for people, which can lose their tail; `call` and `head` are ids and never do. */
const TRIMMABLE = ['label', 'op', 'lane', 'view', 'tool'] as const;

/**
 * The text to send for a frame, or null if it is not a frame the server
 * would take. A frame within every field's bound can still be over 512
 * bytes (64 characters of four bytes each, twice): its longest words lose
 * their tail until it fits, so it still goes.
 */
export const encodePresence = (frame: PresenceIn): string | null => {
  const clean = readPresenceIn(frame) as Record<string, unknown> | null;
  if (!clean) return null;
  let text = JSON.stringify(clean);
  while (bytes(text) > FRAME_BYTES) {
    const longest = TRIMMABLE.filter((k) => typeof clean[k] === 'string' && (clean[k] as string).length > 0)
      .sort((a, b) => bytes(clean[b] as string) - bytes(clean[a] as string))[0];
    if (longest === undefined) return null;
    const chars = Array.from(clean[longest] as string);
    // Enough characters to cover the overflow, and at least one.
    let over = bytes(text) - FRAME_BYTES, cut = 0;
    while (cut < chars.length && over > 0) over -= bytes(chars[chars.length - 1 - cut++]!);
    clean[longest] = chars.slice(0, chars.length - Math.max(1, cut)).join('');
    text = JSON.stringify(clean);
  }
  return text;
};

const readViewer = (v: unknown): Viewer | null => {
  if (!isObj(v) || typeof v.sid !== 'string' || v.sid.length === 0 || v.sid.length > 64) return null;
  return {
    sid: v.sid,
    name: clamp(v.name, 32) || 'someone',
    color: typeof v.color === 'string' && COLOR.test(v.color) ? v.color : '#8a949e',
    keyTail: clamp(v.keyTail, 4) ?? '',
  };
};

/** A PresenceOut frame from the socket, or null if the message is not one. */
export const decodePresenceOut = (message: unknown): PresenceOut | null => {
  let v: unknown = message;
  if (typeof message === 'string') {
    try { v = JSON.parse(message); } catch { return null; }
  }
  if (!isObj(v) || typeof v.type !== 'string') return null;
  switch (v.type) {
    case 'presence.roster': {
      const you = readViewer(v.you);
      if (!you || !Array.isArray(v.peers)) return null;
      return { type: 'presence.roster', you, peers: v.peers.slice(0, 64).map(readViewer).filter((p): p is Viewer => p !== null) };
    }
    case 'presence.joined': {
      const peer = readViewer(v.peer);
      return peer ? { type: 'presence.joined', peer } : null;
    }
    case 'presence.left':
      return typeof v.sid === 'string' ? { type: 'presence.left', sid: v.sid } : null;
    case 'presence': {
      const frame = readPresenceIn(v.frame);
      return typeof v.sid === 'string' && frame ? { type: 'presence', sid: v.sid, frame } : null;
    }
    case 'presence.full':
      return { type: 'presence.full' };
    default:
      return null;
  }
};

// -- the gate -----------------------------------------------------------------------------------

export interface GateDeps {
  readonly now: () => number;
  readonly timers: Timers;
  /** Put a text frame on the socket. */
  readonly write: (text: string) => void;
  readonly perSecond?: number;
  readonly heartbeatMs?: number;
}

type Latest = 'at' | 'over' | 'focus';

/**
 * One socket's outgoing presence. Discrete frames (lift, end, act, director)
 * go in order; cursor-like frames keep only their latest; no more than ten
 * go out in any second (a sliding window, stricter than the server's
 * bucket of ten a second with bursts of twenty, so the server never strikes).
 *
 * Order is the order of offers: an aim (`at`, `over`) waiting when a
 * discrete frame is offered goes ahead of it, so peers never see an aim from
 * before a drop arrive after the drop. `focus` keeps its own pace.
 */
export class PresenceGate {
  private readonly rate: number;
  /** When the last `rate` frames went, oldest first. */
  private readonly sentAt: number[] = [];
  private readonly queue: string[] = [];
  private readonly latest = new Map<Latest, string>();
  private lastFocus = -Infinity;
  private lastSent: number;
  private lastCursor: string | null = null;
  private timer: unknown = null;
  private beat: unknown = null;
  private paused = false;
  private closed = false;

  constructor(private readonly deps: GateDeps) {
    this.rate = deps.perSecond ?? FRAMES_PER_SECOND;
    this.lastSent = deps.now();
    this.armHeartbeat();
  }

  /** Offer a frame. False: refused (closed, paused, malformed, or too many discrete frames waiting). */
  offer(frame: PresenceIn): boolean {
    if (this.closed || this.paused) return false;
    const text = encodePresence(frame);
    if (text === null) return false;
    if (frame.t === 'at' || frame.t === 'over' || frame.t === 'focus') {
      // Kept in the order last offered.
      this.latest.delete(frame.t);
      this.latest.set(frame.t, text);
      if (frame.t !== 'over') this.lastCursor = text;
    } else {
      if (this.queue.length >= 16) return false;
      for (const [kind, waiting] of [...this.latest]) {
        if (kind === 'focus') continue;
        this.queue.push(waiting);
        this.latest.delete(kind);
      }
      this.queue.push(text);
    }
    this.pump();
    return true;
  }

  /** A hidden tab sends nothing; what was waiting is dropped. */
  pause(): void {
    this.paused = true;
    this.queue.length = 0;
    this.latest.clear();
    this.clearTimer();
  }

  /** Back in view: say where it is at once, so peers that aged it out while it was hidden take it back. */
  resume(): void {
    if (this.closed || !this.paused) return;
    this.paused = false;
    if (this.wait() === 0) this.send(this.lastCursor ?? JSON.stringify({ t: 'at' }));
  }

  close(): void {
    this.closed = true;
    this.pause();
    if (this.beat !== null) this.deps.timers.clear(this.beat);
    this.beat = null;
  }

  /** How long until one more frame may go; 0 when one may go now. */
  private wait(): number {
    if (this.sentAt.length < this.rate) return 0;
    return Math.max(0, this.sentAt[0]! + 1000 - this.deps.now());
  }

  private clearTimer(): void {
    if (this.timer !== null) this.deps.timers.clear(this.timer);
    this.timer = null;
  }

  private send(text: string): void {
    const now = this.deps.now();
    this.sentAt.push(now);
    if (this.sentAt.length > this.rate) this.sentAt.shift();
    this.lastSent = now;
    try { this.deps.write(text); } catch { /* the socket is going; its close will say so */ }
  }

  private pump(): void {
    if (this.closed || this.paused) return;
    while (this.wait() === 0 && this.queue.length) this.send(this.queue.shift()!);
    for (const [kind, text] of [...this.latest]) {
      if (this.wait() > 0) break;
      if (kind === 'focus' && this.deps.now() - this.lastFocus < 1000 / FOCUS_PER_SECOND) continue;
      this.latest.delete(kind);
      if (kind === 'focus') this.lastFocus = this.deps.now();
      this.send(text);
    }
    const waiting = this.queue.length > 0 || this.latest.size > 0;
    if (waiting && this.timer === null) {
      const forFocus = Math.max(0, 1000 / FOCUS_PER_SECOND - (this.deps.now() - this.lastFocus));
      const needsSlot = this.queue.length > 0 || this.latest.has('at') || this.latest.has('over');
      const delay = needsSlot ? this.wait() : Math.max(this.wait(), forFocus);
      this.timer = this.deps.timers.set(() => { this.timer = null; this.pump(); }, Math.max(1, Math.ceil(delay)));
    }
  }

  private armHeartbeat(): void {
    const every = this.deps.heartbeatMs ?? HEARTBEAT_MS;
    this.beat = this.deps.timers.set(() => {
      this.beat = null;
      if (this.closed) return;
      // An idle viewer says again where it is, so peers do not age it out.
      if (!this.paused && this.deps.now() - this.lastSent >= every && this.wait() === 0) {
        this.send(this.lastCursor ?? JSON.stringify({ t: 'at' }));
      }
      this.armHeartbeat();
    }, every);
  }
}

// -- the roster -------------------------------------------------------------------------------------

interface Peer { viewer: Viewer; seen: number }
/**
 * `gone` holds peers aged out without a `left`: a tab hidden for a minute
 * sends nothing, and the server says `joined` once per socket, so the next
 * frame from one of them is how it comes back.
 */
interface Room { you: Viewer | null; full: boolean; peers: Map<string, Peer>; gone: Map<string, Viewer>; frames: Map<string, PresenceIn> }

/** How many aged-out peers a World remembers; the server lets no more than 32 sockets watch it. */
const GONE_MAX = 64;

/** The roster, and how the links feed it. */
export interface RosterFeed extends Roster {
  apply(world: string, frame: PresenceOut): void;
  /** Forget a World: its socket closed. */
  drop(world: string): void;
  /** Age out peers no one has heard of for the TTL, and tell listeners. */
  sweep(): void;
  /** Whether the World said it has no room for this socket's presence. */
  full(world: string): boolean;
}

/** People as the HUD shows them: one entry per name and key tail, with how many tabs they have open on one World. */
export const groupViewers = (entries: readonly { world: string; viewer: Viewer; seen: number }[]): { world: string; viewer: Viewer; tabs: number }[] => {
  const people = new Map<string, { world: string; viewer: Viewer; seen: number; perWorld: Map<string, number> }>();
  for (const e of entries) {
    const who = `${e.viewer.name}\u0000${e.viewer.keyTail}`;
    const had = people.get(who);
    if (!had) {
      people.set(who, { world: e.world, viewer: e.viewer, seen: e.seen, perWorld: new Map([[e.world, 1]]) });
      continue;
    }
    had.perWorld.set(e.world, (had.perWorld.get(e.world) ?? 0) + 1);
    if (e.seen > had.seen) { had.world = e.world; had.viewer = e.viewer; had.seen = e.seen; }
  }
  return [...people.values()]
    .sort((a, b) => b.seen - a.seen || a.viewer.name.localeCompare(b.viewer.name))
    .map((p) => ({ world: p.world, viewer: p.viewer, tabs: Math.max(...p.perWorld.values()) }));
};

export const createRoster = (opts: { now: () => number; ttlMs?: number }): RosterFeed => {
  const ttl = opts.ttlMs ?? ROSTER_TTL_MS;
  const rooms = new Map<string, Room>();
  const listeners = new Set<(world: string) => void>();
  const tell = (world: string) => { for (const fn of [...listeners]) { try { fn(world); } catch { /* its own fault */ } } };
  const room = (world: string): Room => {
    let r = rooms.get(world);
    if (!r) { r = { you: null, full: false, peers: new Map(), gone: new Map(), frames: new Map() }; rooms.set(world, r); }
    return r;
  };
  const live = (r: Room): Peer[] => {
    const cutoff = opts.now() - ttl;
    return [...r.peers.values()].filter((p) => p.seen > cutoff);
  };
  const expire = (world: string, r: Room): boolean => {
    const cutoff = opts.now() - ttl;
    let changed = false;
    for (const [sid, p] of r.peers) {
      if (p.seen > cutoff) continue;
      r.peers.delete(sid);
      r.frames.delete(sid);
      r.gone.delete(sid);
      r.gone.set(sid, p.viewer);
      changed = true;
    }
    for (const sid of r.gone.keys()) {
      if (r.gone.size <= GONE_MAX) break;
      r.gone.delete(sid);
    }
    if (changed) tell(world);
    return changed;
  };

  return {
    apply(world, f) {
      const r = room(world);
      const at = opts.now();
      switch (f.type) {
        case 'presence.roster':
          // A fresh roster is the truth: whoever it leaves out has gone, left frame or not.
          r.you = f.you;
          r.full = false;
          r.peers = new Map(f.peers.filter((p) => p.sid !== f.you.sid).map((p) => [p.sid, { viewer: p, seen: at }]));
          r.gone.clear();
          for (const sid of [...r.frames.keys()]) if (!r.peers.has(sid)) r.frames.delete(sid);
          break;
        case 'presence.joined':
          r.gone.delete(f.peer.sid);
          if (f.peer.sid !== r.you?.sid) r.peers.set(f.peer.sid, { viewer: f.peer, seen: at });
          break;
        case 'presence.left':
          r.peers.delete(f.sid);
          r.gone.delete(f.sid);
          r.frames.delete(f.sid);
          break;
        case 'presence': {
          let peer = r.peers.get(f.sid);
          if (!peer) {
            // Aged out while it was quiet, and speaking again: it never left.
            const viewer = r.gone.get(f.sid);
            if (!viewer) return;
            r.gone.delete(f.sid);
            peer = { viewer, seen: at };
            r.peers.set(f.sid, peer);
          }
          peer.seen = at;
          r.frames.set(f.sid, f.frame);
          break;
        }
        case 'presence.full':
          r.full = true;
          break;
      }
      tell(world);
    },
    drop(world) {
      if (rooms.delete(world)) tell(world);
    },
    sweep() {
      for (const [world, r] of rooms) expire(world, r);
    },
    full: (world) => rooms.get(world)?.full ?? false,
    you: (world) => rooms.get(world)?.you ?? null,
    peers(world) {
      const r = rooms.get(world);
      return r ? live(r).map((p) => p.viewer) : [];
    },
    frames(world) {
      const r = rooms.get(world);
      if (!r) return new Map();
      const alive = new Set(live(r).map((p) => p.viewer.sid));
      return new Map([...r.frames].filter(([sid]) => alive.has(sid)));
    },
    all() {
      const entries: { world: string; viewer: Viewer; seen: number }[] = [];
      for (const [world, r] of rooms) for (const p of live(r)) entries.push({ world, viewer: p.viewer, seen: p.seen });
      return groupViewers(entries);
    },
    on(fn): Unsub {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
  };
};
